/*
    Copyright (c) Computer-L.A.N. GmbH (http://www.computer-lan.de).
    Alle Rechte vorbehalten.
*/

import React from "react";
import RadioGroup from "@mui/material/RadioGroup";
import LoadingIndicator from "../../../../Basis/Components/LoadingIndicator";
import RadioInput from "../../../../Basis/Components/RadioInput";
import useExecute from "../../../../Basis/Utils/useExecute";
import AendereFinanzeinstellungen, { AendereFinanzeinstellungenInput } from "../../../../Management/Mandanten/Commands/AendereFinanzeinstellungen";
import LadeEigeneFirmendaten from "../../../../Management/Mandanten/Queries/LadeEigeneFirmendaten";
import Steuerart from "../../../../Shared/Belege/Queries/Steuerart";
import { translationKeys, useTranslation } from "../../Internationalisierung/i18nEinstellungen";
import ActionsBar from "./ActionsBar";
import { useSchnelleinstiegContext } from "./SchnelleinstiegContext";
import SchrittHeader from "./SchrittHeader";
import SchrittText from "./SchrittText";
import SchrittTitel from "./SchrittTitel";
import { QueryResult } from "./utils";

export default function SchnelleinstiegFinanzeinstellungen() {
    const { t } = useTranslation();
    const { WeiterButton } = useSchnelleinstiegContext();
    const [result, setResult] = React.useState<QueryResult<AendereFinanzeinstellungenInput>>({ loading: true });
    const aendereFinanzeinstellungen = useExecute(new AendereFinanzeinstellungen([]));

    new LadeEigeneFirmendaten(data => {
        setResult({
            loading: false,
            data: {
                geaendert: data.mandantGeaendert,
                mandantId: data.mandantId,
                steuerart: data.steuerart,
                standardSteuersatz: data.standardSteuersatz,
                rechnungenAnEUAuslandMitUmsatzsteuerIdSindSteuerfrei: data.rechnungenAnEUAuslandMitUmsatzsteuerIdSindSteuerfrei,
                rechnungenAnNichtEUAuslandSindSteuerfrei: data.rechnungenAnNichtEUAuslandSindSteuerfrei,
                einenBelegProTeilnahmeErzeugen: data.einenBelegProTeilnahmeErzeugen,
                belegeNichtAutomatischVersenden: data.belegeNichtAutomatischVersenden,
            },
        });
    });

    function handleChange(steuerart: Exclude<Steuerart, "">) {
        return (checked: boolean) => {
            if (checked) {
                setResult(prev => {
                    if ("data" in prev) {
                        return {
                            ...prev,
                            data: {
                                ...prev.data,
                                steuerart
                            }
                        }
                    }

                    return prev;
                })
            }
        }
    }

    if (result.loading) {
        return <LoadingIndicator loading />;
    }

    return (
        <>
            <SchrittHeader>
                <SchrittTitel>
                    {t(translationKeys.schnelleinstiegFinanzeinstellungen.titel)}
                </SchrittTitel>
            </SchrittHeader>

            <SchrittText>{t(translationKeys.schnelleinstiegFinanzeinstellungen.text)}</SchrittText>
            <RadioGroup>
                <RadioInput
                    caption={t(translationKeys.schnelleinstiegFinanzeinstellungen.bruttoCaption)}
                    value={result.data.steuerart === "BRUTTO"}
                    onChange={handleChange("BRUTTO")}
                    tooltip={t(translationKeys.schnelleinstiegFinanzeinstellungen.bruttoTootlip)}
                />
                <RadioInput
                    caption={t(translationKeys.schnelleinstiegFinanzeinstellungen.nettoCaption)}
                    value={result.data.steuerart === "NETTO"}
                    onChange={handleChange("NETTO")}
                    tooltip={t(translationKeys.schnelleinstiegFinanzeinstellungen.nettoTootlip)}
                />
                <RadioInput
                    caption={t(translationKeys.schnelleinstiegFinanzeinstellungen.umsatzsteuerbefreitCaption)}
                    value={result.data.steuerart === "UMSATZSTEUERBEFREIT"}
                    onChange={handleChange("UMSATZSTEUERBEFREIT")}
                    tooltip={t(translationKeys.schnelleinstiegFinanzeinstellungen.umsatzsteuerbefreitTootlip)}
                />
            </RadioGroup>
            <ActionsBar>
                <WeiterButton
                    onClick={() => {
                        return aendereFinanzeinstellungen({
                            geaendert: result.data.geaendert,
                            mandantId: result.data.mandantId,
                            steuerart: result.data.steuerart,
                            standardSteuersatz: result.data.standardSteuersatz,
                            rechnungenAnEUAuslandMitUmsatzsteuerIdSindSteuerfrei: result.data.rechnungenAnEUAuslandMitUmsatzsteuerIdSindSteuerfrei,
                            rechnungenAnNichtEUAuslandSindSteuerfrei: result.data.rechnungenAnNichtEUAuslandSindSteuerfrei,
                            einenBelegProTeilnahmeErzeugen: result.data.einenBelegProTeilnahmeErzeugen,
                            belegeNichtAutomatischVersenden: result.data.belegeNichtAutomatischVersenden,
                        });
                    }}
                />
            </ActionsBar>
        </>
    );
}